import { useCallback, useRef, useState } from 'react'

import { TurnstileWidget } from '@/components/turnstile-widget'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { PUBLIC_CONVEX_SITE_URL, PUBLIC_TURNSTILE_SITE_KEY } from '@/lib/env'

const helpTopics = [
  'Migration planning',
  'Multi-location rollout',
  'Payment processor review',
  'White-label or branded setup',
  'Something else',
] as const

type ContactSalesFormState = {
  firstName: string
  lastName: string
  workEmail: string
  organizationName: string
  role: string
  helpTopic: (typeof helpTopics)[number]
  projectDetails: string
  website: string
}

const initialState: ContactSalesFormState = {
  firstName: '',
  lastName: '',
  workEmail: '',
  organizationName: '',
  role: '',
  helpTopic: 'Migration planning',
  projectDetails: '',
  website: '',
}

export function ContactSalesForm() {
  const startedAtRef = useRef(Date.now())
  const [formState, setFormState] = useState(initialState)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  const handleCaptchaToken = useCallback((token: string | null) => {
    setCaptchaToken(token)
  }, [])

  const captchaRequired = Boolean(PUBLIC_TURNSTILE_SITE_KEY)

  function updateField<Key extends keyof ContactSalesFormState>(field: Key, value: ContactSalesFormState[Key]) {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }))
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (captchaRequired && !captchaToken) {
      setErrorMessage('Please complete the verification check before submitting.')
      return
    }

    try {
      setIsSubmitting(true)
      setErrorMessage(null)
      setSuccessMessage(null)

      if (!PUBLIC_CONVEX_SITE_URL) {
        throw new Error('Contact backend is not configured.')
      }
      // POST directly to the Convex site URL — there's no /api proxy on the SPA host.
      const response = await fetch(`${PUBLIC_CONVEX_SITE_URL}/public/contact-sales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          startedAt: startedAtRef.current,
          captchaToken,
        }),
      })
      const result = (await response.json().catch(() => null)) as
        | {
            success: true
          }
        | {
            error: string
          }
        | null

      if (!response.ok) {
        throw new Error(result && 'error' in result ? result.error : 'Could not submit your request.')
      }

      setFormState(initialState)
      startedAtRef.current = Date.now()
      setSuccessMessage('Your request was sent. We will follow up with a practical next step.')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not submit your request.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={formState.website}
        onChange={(event) => updateField('website', event.target.value)}
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="space-y-2">
          <span className="text-sm font-semibold text-ink">First name</span>
          <Input value={formState.firstName} onChange={(event) => updateField('firstName', event.target.value)} placeholder="Amina" required />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-semibold text-ink">Last name</span>
          <Input value={formState.lastName} onChange={(event) => updateField('lastName', event.target.value)} placeholder="Khan" required />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-semibold text-ink">Work email</span>
          <Input
            type="email"
            value={formState.workEmail}
            onChange={(event) => updateField('workEmail', event.target.value)}
            placeholder="you@organization.org"
            required
          />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-semibold text-ink">Organization</span>
          <Input
            value={formState.organizationName}
            onChange={(event) => updateField('organizationName', event.target.value)}
            placeholder="Your organization"
            required
          />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-semibold text-ink">Role</span>
          <Input value={formState.role} onChange={(event) => updateField('role', event.target.value)} placeholder="Development Director" required />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-semibold text-ink">What do you need help with?</span>
          <select
            className="field"
            value={formState.helpTopic}
            onChange={(event) => updateField('helpTopic', event.target.value as ContactSalesFormState['helpTopic'])}
          >
            {helpTopics.map((helpTopic) => (
              <option key={helpTopic}>{helpTopic}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="space-y-2">
        <span className="text-sm font-semibold text-ink">Project details</span>
        <Textarea
          value={formState.projectDetails}
          onChange={(event) => updateField('projectDetails', event.target.value)}
          placeholder="Tell us about your website, current donation tools, rollout questions, or anything your team needs to solve."
          required
        />
      </label>

      {errorMessage ? (
        <p className="rounded-2xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {successMessage ? (
        <p className="rounded-2xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success" role="status">
          {successMessage}
        </p>
      ) : null}

      {captchaRequired ? (
        <TurnstileWidget
          siteKey={PUBLIC_TURNSTILE_SITE_KEY}
          action="contact-sales"
          onToken={handleCaptchaToken}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={isSubmitting || (captchaRequired && !captchaToken)}>
          {isSubmitting ? 'Sending...' : 'Send request'}
        </Button>
        <p className="text-sm leading-6 text-ink-muted">We will reply with a practical next step, not a generic sales sequence.</p>
      </div>
    </form>
  )
}
